import * as React from 'react';
import { TabStyles, ButtonWidget, ButtonType, TextLinkWidget } from '@tableau/widgets';
import { style } from 'typestyle';
import { useCallback, useState } from 'react';
import { SessionUserSelectors, SessionServerSelectors } from '../../../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../../../codegen/Localize';
import { componentRegistryEntry } from '../../../directives/tbSiteSettingsForServerAdminFormAdapter';
import { useSelector } from '../../utils';
import { SettingsSection } from '../SettingsSection';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ServiceLocator } from '../../../../app/ServiceLocator';
import { SiteSettingsForSiteAdminProps } from '../ReactSiteSettingsForSiteAdmin';
import { UserSettingsActions } from '../../../../ts/actions/UserSettingsActions';

export type StartPageSectionProps = SiteSettingsForSiteAdminProps<'siteStartPageUri'>;

const spacerStyle = style({
  paddingBottom: TabStyles.Spacing.MediumSpace
});

export function StartPageSection(props: StartPageSectionProps) {
  const [ siteStartPageUriState, setSiteStartPageUriState ] = useState<string>(props.settings.siteStartPageUri);

  const openResetStartPageDialog = useCallback(() => {
    ServiceLocator.get<UserSettingsActions>('UserSettingsActions').confirmResetSiteDefaultStartPage(() => {
      setSiteStartPageUriState(null);
    });
  }, [setSiteStartPageUriState]);

  if (!useShowSiteStartPage()) {
    return null;
  }

  return (
    <SettingsSection title={Messages.settings_startPage_title()} description={useSelectDescriptionByServerEnvironment()} >
      <div className={spacerStyle} id='current-start-page-id'>
        <span>{Messages.settings_startPage_current_start_page()}</span>
        <TextLinkWidget
          href={`/#${getStartPageUrl(siteStartPageUriState, props)}`}
          children={getStartPageUrl(siteStartPageUriState, props)}
          testId={'default-start-page-link'}
          target='_blank'
        />
        {!siteStartPageUriState &&
        <span className='tb-disabled' data-tb-test-id={'default-start-page-aside'}>
          &nbsp;{Messages.settings_startPage_system_default_aside()}
        </span>}
      </div>
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId='reset-default-site-start-page-button'
        disabled={!siteStartPageUriState}
        handleClick={openResetStartPageDialog}
        children={Messages.settings_startPage_reset_button()}
      />
    </SettingsSection>
  );
}

function useShowSiteStartPage(): boolean {
  const isSessionMultiSite = useSelector<boolean>(SessionUserSelectors.isSessionMultiSite);
  const isOnlineEnvironment = useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment);

  return FeatureFlags.siteDefaultStartPage && (isSessionMultiSite || isOnlineEnvironment);
}

function getStartPageUrl(siteStartPage: string, props: StartPageSectionProps): string {
  // serverSettings.defaultStartPage will currently default to '/' if none is set
  return siteStartPage || props.serverSettings.defaultStartPage;
}

function useSelectDescriptionByServerEnvironment() {
  return useSelector<boolean>(SessionServerSelectors.isOnlineEnvironment)
    ? Messages.settings_siteStartPage_description_online()
    : Messages.settings_siteStartPage_description_server();
}

/* for registering the component in settings registry */
export const StartPageSpec = componentRegistryEntry({
  name: 'StartPageSection',
  component: StartPageSection
});

